import { useEffect } from 'react';

const SITE_URL = 'https://campchefpro.com';
const SITE_NAME = 'CampChef Pro';
const DEFAULT_IMAGE =
  'https://szbvvvxyamindcuymrcr.supabase.co/storage/v1/object/public/brand-assets//horiz%20logo.png';
const TWITTER_HANDLE = '@campchefpro';
const PRIMARY_HOST = 'campchefpro.com';

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export interface SeoOptions {
  title: string;
  description: string;
  /** Path beginning with `/`. Used for canonical + og:url. */
  path: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageAlt?: string;
  /** og:type — defaults to "website". */
  type?: string;
  /** Discourage indexing (e.g. /auth). */
  noIndex?: boolean;
  /** Optional JSON-LD object(s). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Optional breadcrumb trail; emitted as BreadcrumbList JSON-LD. */
  breadcrumbs?: BreadcrumbItem[];
}

const upsertMeta = (selector: string, attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute('data-managed', 'seo');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertName = (key: string, content: string) =>
  upsertMeta(`meta[name="${key}"]`, 'name', key, content);

const upsertProperty = (key: string, content: string) =>
  upsertMeta(`meta[property="${key}"]`, 'property', key, content);

const upsertCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    el.setAttribute('data-managed', 'seo');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const JSON_LD_ATTR = 'seo-json-ld';
const upsertJsonLdBlocks = (blocks: Record<string, unknown>[]) => {
  // Remove all previously managed JSON-LD scripts.
  document.head
    .querySelectorAll(`script[data-managed="${JSON_LD_ATTR}"]`)
    .forEach((el) => el.remove());
  // Also clean up the legacy single-script id, if present.
  document.getElementById('seo-json-ld')?.remove();

  blocks.forEach((block) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-managed', JSON_LD_ATTR);
    script.textContent = JSON.stringify(block);
    document.head.appendChild(script);
  });
};

const isPrimaryHost = () =>
  typeof window === 'undefined' || window.location.hostname === PRIMARY_HOST;

export function useSeo(opts: SeoOptions) {
  const {
    title,
    description,
    path,
    image = DEFAULT_IMAGE,
    imageWidth = 1200,
    imageHeight = 630,
    imageAlt,
    type = 'website',
    noIndex,
    jsonLd,
    breadcrumbs,
  } = opts;

  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const altText = imageAlt ?? title;

    document.title = title;

    upsertName('description', description);
    upsertCanonical(url);

    upsertProperty('og:title', title);
    upsertProperty('og:description', description);
    upsertProperty('og:type', type);
    upsertProperty('og:url', url);
    upsertProperty('og:image', image);
    upsertProperty('og:image:width', String(imageWidth));
    upsertProperty('og:image:height', String(imageHeight));
    upsertProperty('og:image:alt', altText);
    upsertProperty('og:site_name', SITE_NAME);
    upsertProperty('og:locale', 'en_US');

    upsertName('twitter:card', 'summary_large_image');
    upsertName('twitter:site', TWITTER_HANDLE);
    upsertName('twitter:title', title);
    upsertName('twitter:description', description);
    upsertName('twitter:image', image);
    upsertName('twitter:image:alt', altText);

    // Force noindex on non-primary hosts (e.g. lovable.app preview) to prevent duplicate indexing.
    const effectiveNoIndex = noIndex || !isPrimaryHost();
    if (effectiveNoIndex) {
      upsertName('robots', 'noindex,nofollow');
    } else {
      const robots = document.head.querySelector('meta[name="robots"]');
      if (robots?.getAttribute('data-managed') === 'seo') robots.remove();
    }

    // Compose JSON-LD: caller-provided + optional BreadcrumbList.
    const blocks: Record<string, unknown>[] = [];
    if (jsonLd) {
      if (Array.isArray(jsonLd)) blocks.push(...jsonLd);
      else blocks.push(jsonLd);
    }
    if (breadcrumbs && breadcrumbs.length > 0) {
      blocks.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: `${SITE_URL}${b.path}`,
        })),
      });
    }
    upsertJsonLdBlocks(blocks);
  }, [
    title,
    description,
    path,
    image,
    imageWidth,
    imageHeight,
    imageAlt,
    type,
    noIndex,
    JSON.stringify(jsonLd),
    JSON.stringify(breadcrumbs),
  ]);
}
